import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import type { PowerData, PowerChartData } from '@/api/dashboard';
import { fetchDrillChart } from '@/api/dashboard';
import { useI18n } from '@/i18n';
import { localizeChartLabel } from '@/lib/chartLabel';
import { cn } from '@/lib/utils';
import { buildProductionPeriod, type ProductionBucket, type ProductionPeriod } from '@/api/inverterProduction';

const TABS = [
  { key: 'Hourly', labelKey: 'home.chart.hourly', color: '#8b5cf6', unit: 'kWh' },
  { key: 'Daily', labelKey: 'home.chart.daily', color: '#14b8a6', unit: 'MWh' },
  { key: 'Monthly', labelKey: 'home.chart.monthly', color: '#3b82f6', unit: 'MWh' },
  { key: 'Yearly', labelKey: 'home.chart.yearly', color: '#f59e0b', unit: 'MWh' },
] as const;
type Tab = typeof TABS[number]['key'];

interface Props {
  data: PowerChartData;
  tagIds?: number[];
  plantUuidId?: string | null;
  compact?: boolean;
  className?: string;
  titleClassName?: string;
  drillFetcher?: (year: number, month: number | null, day: number | null) => Promise<PowerData[]>;
  showHourly?: boolean;
  expectedHourly?: PowerData[];
  openMeteoExpectedHourly?: PowerData[];
  initialTab?: Tab;
  expectedFetcher?: (tab: 'Daily' | 'Monthly') => Promise<PowerData[]>;
  onPeriodChange?: (period: ProductionPeriod) => void;
  onDrillLoadingChange?: (loading: boolean) => void;
}

function HighchartsBarChart({ options, height }: { options: Highcharts.Options; height: number }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const chart = Highcharts.chart(containerRef.current, options);
    return () => chart.destroy();
  }, [options]);

  return <div ref={containerRef} style={{ height: `${height}px` }} className="w-full" />;
}

export default function PowerBarChart({
  data,
  tagIds = [],
  plantUuidId = null,
  compact,
  className,
  titleClassName,
  drillFetcher,
  showHourly = true,
  expectedHourly,
  openMeteoExpectedHourly,
  initialTab = 'Monthly',
  expectedFetcher,
  onPeriodChange,
  onDrillLoadingChange,
}: Props) {
  const { t } = useI18n();
  const tFn = t;
  const [tab, setTab] = useState<Tab>(initialTab);
  const [drillYear, setDrillYear] = useState<number | null>(null);
  const [drillMonth, setDrillMonth] = useState<number | null>(null);
  const [drillDay, setDrillDay] = useState<number | null>(null);
  const [drillData, setDrillData] = useState<PowerData[] | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [lazyExpected, setLazyExpected] = useState<{ Daily?: PowerData[]; Monthly?: PowerData[] }>({});

  const visibleTabs = useMemo(
    () => (showHourly ? TABS : TABS.filter((item) => item.key !== 'Hourly')),
    [showHourly],
  );

  function handleTabClick(newTab: Tab) {
    setTab(newTab);
    setDrillYear(null);
    setDrillMonth(null);
    setDrillDay(null);
    setDrillData(null);
    if ((newTab === 'Daily' || newTab === 'Monthly') && expectedFetcher && !lazyExpected[newTab]) {
      void expectedFetcher(newTab)
        .then((rows) => setLazyExpected((current) => ({ ...current, [newTab]: rows })))
    }
  }

  const doDrill = useCallback(async (year: number, month: number | null, day: number | null) => {
    setDrillLoading(true);
    try {
      const rows = drillFetcher
        ? await drillFetcher(year, month, day)
        : await fetchDrillChart(year, month, day, tagIds, plantUuidId);
      setDrillData(rows);
    } finally {
      setDrillLoading(false);
    }
  }, [drillFetcher, plantUuidId, tagIds]);

  // Refs to always hold current values in Highcharts event handlers (stale closure fix)
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const doDrillRef = useRef(doDrill);
  doDrillRef.current = doDrill;

  const chartData = useMemo(() => {
    if (tab === 'Yearly') return data.yearly;
    if (drillData !== null) return drillData;
    if (tab === 'Hourly') return data.hourly ?? [];
    if (tab === 'Monthly') return data.monthly;
    return data.daily;
  }, [tab, data, drillData]);

  const productionPeriod = useMemo(() => buildProductionPeriod(
    ({ Yearly: 'year', Monthly: 'month', Daily: 'day', Hourly: 'hour' } as Record<Tab, ProductionBucket>)[tab], chartData,
  ), [tab, chartData]);
  useEffect(() => { if (!drillLoading) onPeriodChange?.(productionPeriod); }, [productionPeriod, drillLoading, onPeriodChange]);
  useEffect(() => { onDrillLoadingChange?.(drillLoading); }, [drillLoading, onDrillLoadingChange]);

  // Daily and monthly estimates are still calculated lazily for later use,
  // but remain hidden until their chart presentation is finalized.
  const expectedData = tab === 'Hourly' ? expectedHourly : undefined;
  const openMeteoData = tab === 'Hourly' ? openMeteoExpectedHourly : undefined;

  const localizedCategories = useMemo(
    () => chartData.map((item) => localizeChartLabel(item.date_name, t)),
    [chartData, t],
  );

  const activeTab = visibleTabs.find((item) => item.key === tab) ?? visibleTabs[0];
  const activeColor = activeTab.color;
  const chartHeight = compact ? 130 : 256;

  // Breadcrumb label for drilled state
  const drillLabel = useMemo(() => {
    if (tab === 'Hourly' && drillYear !== null && drillMonth !== null && drillDay !== null) {
      const dayItem = data.daily.find((d) =>
        d.date_year === drillYear && d.date_month === drillMonth && d.date_day === drillDay
      );
      return localizeChartLabel(dayItem?.date_name ?? `${drillDay}`, t);
    }
    if (tab === 'Daily' && drillYear !== null && drillMonth !== null) {
      const monthItem = data.monthly.find(d => d.date_year === drillYear && d.date_month === drillMonth);
      return `${localizeChartLabel(monthItem?.date_name ?? String(drillMonth), t)} ${drillYear}`;
    }
    if (tab === 'Monthly' && drillYear !== null) {
      return `${drillYear}`;
    }
    return null;
  }, [tab, drillYear, drillMonth, drillDay, data.daily, data.monthly]);

  function handleBack() {
    if (tab === 'Hourly') {
      setTab('Daily');
      setDrillDay(null);
      setDrillData(null);
      if (drillYear !== null && drillMonth !== null) void doDrill(drillYear, drillMonth, null);
    } else if (tab === 'Daily') {
      setTab('Monthly');
      setDrillMonth(null);
      setDrillDay(null);
      setDrillData(null);
      if (drillYear !== null) void doDrill(drillYear, null, null);
    } else if (tab === 'Monthly') {
      setTab('Yearly');
      setDrillYear(null);
      setDrillMonth(null);
      setDrillDay(null);
      setDrillData(null);
    }
  }

  const highchartsOptions = useMemo<Highcharts.Options>(() => ({
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      spacingTop: 5,
      spacingLeft: 0,
      spacingRight: 0,
      spacingBottom: 5,
      style: {
        fontFamily: 'Inter, system-ui, sans-serif',
      },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: Boolean(expectedData?.length || openMeteoData?.length) },
    xAxis: {
      categories: localizedCategories,
      lineColor: '#e2e8f0',
      tickColor: '#e2e8f0',
      labels: {
        style: {
          color: '#64748b',
          fontSize: compact ? '9px' : '11px',
          fontWeight: '600',
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#f8fafc',
      gridLineDashStyle: 'Dash',
      labels: {
        style: {
          color: '#94a3b8',
          fontSize: compact ? '9px' : '11px',
        },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      borderRadius: 16,
      borderColor: '#cbd5e1',
      backgroundColor: 'rgba(255,255,255,0.96)',
      shadow: false,
      padding: 0,
      formatter() {
        const points = this.points ?? [];
        const rows = points.map((point) => `
          <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:5px">
            <span style="font-size:12px;color:#0f172a">
              <span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${point.color};margin-right:6px"></span>
              ${point.series.name}
            </span>
            <span style="font-size:14px;font-weight:700;color:#0f172a">${typeof point.y === 'number' ? point.y.toFixed(3) : '-'} ${activeTab.unit}</span>
          </div>
        `).join('');
        return `
          <div style="min-width:140px;padding:12px 14px">
            <div style="font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:#64748b;margin-bottom:6px">
              ${this.x ?? ''}
            </div>
            ${rows}
          </div>
        `;
      },
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        borderRadius: 3,
        maxPointWidth: compact ? 28 : 40,
        pointPadding: 0.08,
        groupPadding: 0.15,
        dataLabels: {
          enabled: true,
          inside: false,
          padding: 2,
          style: {
            color: '#475569',
            fontSize: compact ? '9px' : '11px',
            fontWeight: '600',
            textOutline: 'none',
          },
          formatter: function () {
            return this.y == null ? undefined : `${this.y.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} ${activeTab.unit}`;
          },
        },
        cursor: tab === 'Hourly' ? 'default' : 'pointer',
        point: {
          events: {
            click: function () {
              const currentTab = tabRef.current;
              const idx = this.index;
              const item = chartData[idx];
              if (!item) return;

              if (currentTab === 'Yearly' && item.date_year != null) {
                setDrillYear(item.date_year);
                setDrillMonth(null);
                setDrillDay(null);
                setDrillData(null);
                setTab('Monthly');
                void doDrill(item.date_year, null, null);
              } else if (currentTab === 'Monthly' && item.date_year != null && item.date_month != null) {
                setDrillYear(item.date_year);
                setDrillMonth(item.date_month);
                setDrillDay(null);
                setDrillData(null);
                setTab('Daily');
                void doDrill(item.date_year, item.date_month, null);
              } else if (currentTab === 'Daily' && item.date_year != null && item.date_month != null && item.date_day != null) {
                setDrillYear(item.date_year);
                setDrillMonth(item.date_month);
                setDrillDay(item.date_day);
                setDrillData(null);
                setTab('Hourly');
                void doDrill(item.date_year, item.date_month, item.date_day);
              }
            },
          },
        },
      },
      series: {
        animation: { duration: 350 },
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    series: [
      {
        type: 'column',
        name: t('home.chart.tooltip'),
        color: activeColor,
        data: chartData.map((item) => Number(item.power_total ?? 0)),
      },
      ...(expectedData?.length
        ? [{
            type: 'column' as const,
            name: 'Beklenen üretim (pvlib)',
            color: '#f59e0b',
            data: chartData.map((item) => {
              const expected = expectedData.find((row) => row.date_name === item.date_name);
              return expected ? Number(expected.power_total ?? 0) : null;
            }),
          }]
        : []),
      ...(openMeteoData?.length
        ? [{
            type: 'column' as const,
            name: 'Beklenen üretim (Open-Meteo 15 dk)',
            color: '#0ea5e9',
            data: chartData.map((item) => {
              const expected = openMeteoData.find((row) => row.date_name === item.date_name);
              return expected ? Number(expected.power_total ?? 0) : null;
            }),
          }]
        : []),
    ],
  }), [activeColor, activeTab.unit, chartData, localizedCategories, t, compact, tab, expectedData, openMeteoData]);

  return (
    <div className={cn(
      "rounded-[28px] border border-slate-100 bg-white shadow-sm transition-all flex flex-col",
      compact ? "p-3 h-[200px]" : "p-6 shadow-[0_20px_60px_-32px_rgba(15,23,42,0.35)]",
      className
    )}>
      <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between", compact ? "mb-2" : "mb-4")}>
        <div className="flex items-center gap-2 min-w-0">
          {drillLabel && (
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-[9px] font-bold text-slate-400 hover:text-slate-700 transition-colors shrink-0"
            >
              ←
            </button>
          )}
          <h2 className={cn("text-[12px] font-black text-slate-900 tracking-tight leading-none truncate", titleClassName)}>
            {t('common.powerStatus')}
            {drillLabel && (
              <span className="ml-1.5 text-[10px] font-semibold text-slate-400">{drillLabel}</span>
            )}
          </h2>
        </div>
        <div className="flex overflow-hidden rounded-xl border border-slate-100 bg-slate-50/50 shadow-sm">
          {visibleTabs.map((tabDef) => (
            <button
              key={tabDef.key}
              onClick={() => handleTabClick(tabDef.key)}
              className={cn("px-3 py-1 text-[9px] font-bold transition-all",
                tab === tabDef.key
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'text-slate-500 hover:bg-white hover:text-slate-900'
              )}
            >
              {tFn(tabDef.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="relative flex-1">
        <HighchartsBarChart options={highchartsOptions} height={chartHeight} />
        {drillLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
            <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
          </div>
        )}
      </div>

      {!compact && (
        <p className="mt-4 text-sm text-slate-400">
          {tab === 'Hourly'
            ? t('home.chart.rangeHourly')
            : tab === 'Daily'
              ? t('home.chart.rangeDaily')
              : tab === 'Monthly'
                ? t('home.chart.rangeMonthly')
                : t('home.chart.rangeYearly')}
        </p>
      )}
    </div>
  );
}
